import { addDays, fleetInstant, fleetToday, toWallClock, wallClock } from '@vlm/api';
import { useEffect, useState } from 'react';
import { env } from '../env';
import { useT } from '../i18n';

/** The API's wall-clock strings, printed in the language on screen, exactly as the fleet's clock read them. */
export function useTimes() {
  const { fmt } = useT();
  return {
    dateTime: (value: string | null | undefined, withSeconds = false) => fmt.dateTime(wallClock(value), withSeconds),
    date: (value: string | null | undefined) => fmt.date(wallClock(value)),
    time: (value: string | null | undefined, withSeconds = false) => fmt.time(wallClock(value), withSeconds),
    /** "12 sec ago" — the real moment, read in the fleet's zone. A report newer than the ticking clock reads as now, never "in 6 sec". */
    ago: (value: string | null | undefined, now = Date.now()) => {
      const instant = fleetInstant(value, env.fleetZone);
      return fmt.relative(instant, instant ? Math.max(now, instant.getTime()) : now);
    },
  };
}

/** Re-renders every `ms`, for relative times ("12 sec ago") that must keep counting. */
export function useNow(ms = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

/** Today in the fleet's zone, "2026-10-20". */
export const today = () => fleetToday(env.fleetZone);

/** The fleet's wall clock now, "2026-10-20T18:59". */
export function nowLocalMinute(): string {
  const w = toWallClock(new Date(), env.fleetZone);
  return w.toISOString().slice(0, 16);
}

export type Preset = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth';

/** A preset period as [from, to] dates in the fleet's zone (both ends included). */
export function presetRange(preset: Preset): [string, string] {
  const t = today();
  switch (preset) {
    case 'today':
      return [t, t];
    case 'yesterday':
      return [addDays(t, -1), addDays(t, -1)];
    case 'last7':
      return [addDays(t, -6), t];
    case 'last30':
      return [addDays(t, -29), t];
    case 'thisMonth':
      return [`${t.slice(0, 8)}01`, t];
  }
}

export const PRESETS: readonly Preset[] = ['today', 'yesterday', 'last7', 'last30', 'thisMonth'];

/** The API's window for whole days: from 00:00:00 to 23:59:59 (GET /api/alerts does not stretch a bare end date). */
export const dayStart = (date: string) => `${date}T00:00:00`;
export const dayEnd = (date: string) => `${date}T23:59:59`;

/** A real calendar date, "2026-10-20" (not "2026-13-01", which Date would quietly roll over). */
export function isDate(value: string | null | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return wallClock(value)?.toISOString().slice(0, 10) === value;
}

/** "2026-10-20T18:59" (datetime-local) → the API's "2026-10-20T18:59:00". */
export const localToParam = (value: string) => (value.length === 16 ? `${value}:00` : value);
